/**
 * LogoSpinner — central SmartNote2Self logo with a teal arc rotating clockwise
 * around it. Used on loading surfaces (assessment fetch, report generation,
 * summary fetch) so longer waits feel intentional rather than dead.
 *
 * - `size`     – overall diameter in px (default 112).
 * - `label`    – optional short status line shown directly below the spinner.
 * - `sublabel` – optional secondary line (e.g. "Elapsed: 24s").
 */
export default function LogoSpinner({ size = 112, label, sublabel }) {
  const stroke = Math.max(3, Math.round(size / 28));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  // Arc covers ~30% of the circle; the rest stays as the track. Rotated by
  // tailwind's animate-spin → arc travels clockwise around the logo.
  const arcLength = circumference * 0.3;

  return (
    <div className="inline-flex flex-col items-center" data-testid="logo-spinner">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Track ring */}
        <svg
          className="absolute inset-0"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgb(226 232 240)"
            strokeWidth={stroke}
          />
        </svg>

        {/* Rotating teal arc — clockwise */}
        <svg
          className="absolute inset-0 animate-spin"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ animationDuration: "1.6s" }}
          aria-hidden="true"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgb(13 148 136)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${arcLength} ${circumference - arcLength}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>

        {/* Centred logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src="/logo.png"
            alt=""
            className="rounded-md object-cover shadow-sm"
            style={{ width: size * 0.46, height: size * 0.46 }}
            draggable={false}
          />
        </div>
      </div>

      {label && (
        <p
          data-testid="logo-spinner-label"
          className="mt-5 text-sm font-medium text-slate-700 dark:text-slate-200 text-center"
        >
          {label}
        </p>
      )}
      {sublabel && (
        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 text-center">
          {sublabel}
        </p>
      )}
    </div>
  );
}
